import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import DayCard from '../../components/agent/DayCard'
import HoursSummary from '../../components/agent/HoursSummary'
import { totalScheduledHours, type Category, type DayEntry } from '../../components/agent/scheduleDay'
import { supabase } from '../../lib/supabase'
import { PAY_PERIOD as PAY_PERIOD_19, PAY_PERIOD_DATES } from './payPeriod19'
import { dateRangeLabel, periodDates, periodNumberFor, type PeriodDateSkeleton } from '../../lib/payPeriods'
import { getMyScheduleDays, unscheduledDay } from './agentScheduleStore'
import { getMyScheduleDefault, type EmployeeScheduleDefault } from '../supervisor/scheduleDefaultStore'
import { PAY_PERIOD_20, PAY_PERIOD_20_DATES } from './payPeriod20'
import { getPendingDraft, savePendingDraft, type PendingDraft } from './pendingDraftStore'
import { isPeriodPast } from './payPeriodDates'
import { getEditableScheduleSubmissionDays, getMyPlacementStart, getMyScheduleSubmissions, reopenSchedule, submitSchedule } from './scheduleSubmissionStore'
import { useProfileIdentity } from '../../lib/useProfileIdentity'

/**
 * Builds a pay period, docs/product/scheduling-model.md's "Copying a
 * schedule": choose a source, review the result, adjust individual days or
 * several at once with the quick-select statuses, then save it as a draft
 * and submit it to a supervisor.
 *
 * The period built is normally the next one, since the current period is
 * usually already scheduled by the time an agent gets here. But
 * docs/product/scheduling-model.md's "Request horizon" also lets an agent
 * edit and submit the current pay period, and an agent with nothing in it
 * yet needs a way in: the dashboard's own Build your current schedule
 * action, shown only then, names it explicitly with a `period=current`
 * search param (its Build Your Next Pay Period action does the same with
 * `period=next`). Reached with neither, this falls back to naming it from
 * the agent's own real data instead, once it loads: the current period
 * when it holds none of the agent's own schedule entries, the next one
 * otherwise.
 *
 * A `reopen` search param, set by the request history page's own Edit
 * action on a submission awaiting a decision, or Reopen action on a
 * returned one, loads that submission's own real day-by-day content into
 * this same saved-draft editing view instead of a fresh source step: the
 * fields prefill from schedule_entries, a returned submission's
 * supervisor comment shows above them, and Submit calls reopenSchedule
 * instead of submitSchedule.
 */

// The actual current date, same derivation as this file's sibling agent
// pages.
const now = new Date()
const REAL_TODAY_ISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

// The 28 days ending with the current period: the previous pay period and
// the current one, what the agent's own history offers to copy from. A day
// none of the agent's schedule entries covers reads as unscheduled.
const PREVIOUS_PERIOD_DATES = periodDates(periodNumberFor(REAL_TODAY_ISO) - 1)
const HISTORY_DATES = [...PREVIOUS_PERIOD_DATES, ...PAY_PERIOD_DATES]

type SourceKey = 'period' | 'week' | 'month' | 'custom' | 'default'

const SOURCE_ORDER: SourceKey[] = ['period', 'week', 'month', 'custom', 'default']

const SOURCE_LABEL: Record<SourceKey, string> = {
  period: 'Copy Prior Pay Period',
  week: 'Prior week',
  month: 'Prior month',
  custom: 'Custom range',
  default: 'Your normal schedule',
}

// Each option names the dates it copies from; one with no schedule entry of
// the agent's own behind it stays in the menu, per the copying spec, but is
// not selectable.
const SOURCE_DETAIL: Record<'period' | 'week' | 'month' | 'default', string> = {
  period: `${PAY_PERIOD_19.label} · ${PAY_PERIOD_19.range}`,
  week: dateRangeLabel(PAY_PERIOD_DATES.slice(7, 14)),
  month: dateRangeLabel(HISTORY_DATES),
  default: 'Built from the shift and days off set for you',
}

type QuickEdit = { category: Category; label: string; detail?: string }

// The four categories docs/design/journey-map.md's "Building a schedule"
// step 5 calls "quick-select status buttons": the two that already make up
// every copied day (work, off), plus the two exceptions an agent adjusts a
// day to most often. Detail and unavailable stay reachable only through a
// fuller day-editing form, not built yet, rather than guessing at specifics
// (a detail assignment, an unavailability reason) a one-tap action cannot
// actually capture. Work carries the agent's own shift time, the same label
// every work entry does; with no shift on record there are no hours to give a
// workday, and a workday without hours is refused at submit, so Work is not
// offered.
function quickStatusesFor(workLabel: string | null): { key: Category; label: string; patch: QuickEdit; disabled?: boolean }[] {
  return [
    { key: 'work', label: 'Work', patch: { category: 'work', label: workLabel ?? '' }, disabled: workLabel === null },
    { key: 'off', label: 'Off', patch: { category: 'off', label: 'Regular day off' } },
    { key: 'leave', label: 'Leave', patch: { category: 'leave', label: 'Annual leave', detail: 'Full day' } },
    // A real time range, not just the word "Training": scheduledHours only
    // ever reads an "HHMM–HHMM" prefix off the label, the same as every
    // work entry does, so a quick-selected training day still counts toward
    // the week and pay-period totals instead of reading as zero hours.
    { key: 'training', label: 'Training', patch: { category: 'training', label: '0800–1600 · Training' } },
  ]
}

function parseISO(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function formatDateShort(date: string): string {
  return parseISO(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// The agent's own default for a date's weekday (scheduleDefaultStore.ts): a
// normal workday with the assignment they normally work, or a regular day
// off. It is what a day falls back to whenever nothing more specific was
// copied, so it is the sound default for a quick-selected Work too, rather
// than leaving the assignment blank. An agent with no default reads as a
// regular day off, since there is nothing to invent one from.
function defaultDayFor(date: string, scheduleDefault: EmployeeScheduleDefault | null): { category: Category; label: string; detail?: string } {
  const weekdayName = parseISO(date).toLocaleDateString('en-US', { weekday: 'long' })
  const weekday = scheduleDefault?.weekly.find((day) => day.weekday === weekdayName)
  if (weekday?.category === 'work' && scheduleDefault?.shift) {
    return { category: 'work', label: scheduleDefault.shift.time, detail: weekday.assignment }
  }
  return { category: 'off', label: 'Regular day off' }
}

// docs/product/scheduling-model.md's "Scheduled hours": totaled by week and
// by pay period on this same review screen, whichever 14 days are on it.
function hoursSummaryRows(days: DayEntry[]): { rows: { label: string; hours: number }[]; total: number } {
  return {
    rows: [
      { label: 'Week 1', hours: totalScheduledHours(days.slice(0, 7)) },
      { label: 'Week 2', hours: totalScheduledHours(days.slice(7, 14)) },
    ],
    total: totalScheduledHours(days),
  }
}

// The dates a custom range covers, up to the target's own length: each one
// is read against the agent's own schedule entries, and whichever have none
// come back as gaps, exactly like running out of a short source early. Every
// pay period is the same length, so this holds regardless of which one is
// the actual build target.
// String comparison is safe here because every date is a zero-padded ISO
// string, where lexicographic order matches calendar order.
function customDateSkeletons(start: string, end: string): PeriodDateSkeleton[] {
  if (!start || !end || end < start) return []

  const dates: PeriodDateSkeleton[] = []
  const cursor = parseISO(start)
  const endDate = parseISO(end)

  while (cursor <= endDate && dates.length < PAY_PERIOD_20_DATES.length) {
    dates.push({
      date: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
      weekday: cursor.toLocaleDateString('en-US', { weekday: 'short' }),
      day: cursor.getDate(),
      month: cursor.toLocaleDateString('en-US', { month: 'long' }),
    })
    cursor.setDate(cursor.getDate() + 1)
  }

  return dates
}

// Maps the chosen source's days onto the target period by position, per the
// copying spec: day one of the source becomes day one of the target. A
// source day that is not a normal shift or a regular day off (an exception
// like leave or training, or a day with no entry), or a position past the
// end of a shorter source, is not something a copy carries forward, so both
// fall back to the agent's own default for that weekday. A source longer than
// the target simply has nothing to map its extra days onto, so they are
// never read. Every result comes back as a fresh, unapproved draft: the spec
// excludes prior approval records and comments.
function buildPreview(
  source: (DayEntry | undefined)[],
  scheduleDefault: EmployeeScheduleDefault | null,
  targetDates: PeriodDateSkeleton[],
): DayEntry[] {
  return targetDates.map((target, index) => {
    const candidate = source[index]
    const base =
      candidate && (candidate.category === 'work' || candidate.category === 'off')
        ? candidate
        : defaultDayFor(target.date, scheduleDefault)

    return {
      ...base,
      date: target.date,
      weekday: target.weekday,
      day: target.day,
      month: target.month,
      state: 'draft',
    }
  })
}

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

function TriangleAlertIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

// Selecting is a toggle button around the whole card rather than a separate
// checkbox input: the accessible name comes from the card's own visible
// text (weekday, day, status, detail), so a screen reader announces exactly
// what is being selected without a second, hand-written label to keep in
// sync with it. The checkbox glyph is decorative; aria-pressed carries the
// actual state.
function SelectableDayCard({
  entry,
  selected,
  flagged,
  onToggle,
}: {
  entry: DayEntry
  selected: boolean
  flagged: boolean
  onToggle: () => void
}) {
  return (
    <button type="button" onClick={onToggle} aria-pressed={selected} className="flex w-full items-center gap-2.5 text-left">
      <span
        aria-hidden="true"
        className={`flex size-6 flex-none items-center justify-center rounded border-2 ${
          selected ? 'border-primary bg-primary text-primary-ink' : 'border-line-strong'
        }`}
      >
        {selected && <CheckIcon />}
      </span>
      <span className="min-w-0 flex-1">
        <DayCard entry={entry} flagged={flagged} />
      </span>
    </button>
  )
}

function SourceCard({
  label,
  detail,
  selected,
  onSelect,
  disabled = false,
  children,
}: {
  label: string
  detail: string
  selected: boolean
  onSelect: () => void
  /** No source data exists behind this option yet; present in the menu, per the copying spec, but not selectable. */
  disabled?: boolean
  children?: React.ReactNode
}) {
  const inputId = `copy-source-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div
      className={`rounded-surface border p-3.5 ${disabled ? 'opacity-50' : ''} ${
        selected ? 'border-primary bg-selected' : 'border-line'
      }`}
    >
      <label htmlFor={inputId} className={`flex min-h-11 items-center gap-3 ${disabled ? '' : 'cursor-pointer'}`}>
        <input
          id={inputId}
          type="radio"
          name="copy-source"
          checked={selected}
          disabled={disabled}
          onChange={onSelect}
          className="size-4.5 flex-none accent-primary"
        />
        <span>
          <span className={`block text-small ${selected ? 'font-extrabold' : 'font-bold'}`}>{label}</span>
          <span className="mt-0.5 block text-caption text-ink-muted">{detail}</span>
        </span>
      </label>
      {children}
    </div>
  )
}

export default function AgentCopySchedulePage() {
  const navigate = useNavigate()
  const identity = useProfileIdentity()
  const [searchParams] = useSearchParams()
  const reopenId = searchParams.get('reopen')
  // Set by the dashboard's own two build actions: 'next' for Build Your
  // Next Pay Period, 'current' for Build your current schedule (shown only
  // when the current period is empty). Absent for a bookmark or a reload
  // with no query string, which falls back to the auto-detection below the
  // same way this screen always behaved before either action named its
  // own target explicitly.
  const periodParam = searchParams.get('period')
  // The draft is keyed by the signed-in agent, so it has to wait for the
  // session before it can be read or written; RequireRole has already
  // resolved one by the time this screen mounts; this just reads it back.
  const [userId, setUserId] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  // Which option the source step has selected: the prior period unless the agent has no schedule of their own to copy.
  const [source, setSource] = useState<SourceKey>('period')
  // The agent's own real schedule: the 28 days ending with the current
  // period (what a copy reads from), the target period's own entries (what a
  // copy would replace), and their default week and shift. Read with the
  // session, so the source step renders with the right options enabled.
  const [history, setHistory] = useState<DayEntry[]>(() => HISTORY_DATES.map(unscheduledDay))
  const [existingTargetDates, setExistingTargetDates] = useState<string[]>([])
  const [scheduleDefault, setScheduleDefault] = useState<EmployeeScheduleDefault | null>(null)
  // A draft already sitting in storage when this screen opens is something
  // the agent saved on an earlier visit: land on it directly instead of a
  // blank source step, so Save Draft's confirmation actually leads
  // somewhere the agent can reopen or view it, not just a promise.
  const [savedDraft, setSavedDraft] = useState<PendingDraft | null>(null)
  const [step, setStep] = useState<'source' | 'review'>('source')
  // The supervisor's own return comment and name, shown above the editor
  // while reopening: set only once the real returned submission behind
  // reopenId has actually loaded, since savedDraft's own PendingDraft
  // shape carries no room for either.
  const [reopenContext, setReopenContext] = useState<{ comment?: string; decidedByName?: string; awaitingDecision: boolean } | null>(null)
  // Which pay period this screen actually builds. Explicit when periodParam
  // names one; otherwise the ordinary case (next period) until the agent's
  // own real data loads, below, which then builds the current period
  // instead if it turns out to hold nothing of the agent's own.
  const [targetsCurrentPeriod, setTargetsCurrentPeriod] = useState(periodParam === 'current')
  const TARGET_PERIOD = targetsCurrentPeriod ? PAY_PERIOD_19 : PAY_PERIOD_20
  const TARGET_DATES = targetsCurrentPeriod ? PAY_PERIOD_DATES : PAY_PERIOD_20_DATES
  const TARGET_PERIOD_IS_PAST = isPeriodPast(TARGET_DATES[TARGET_DATES.length - 1].date, REAL_TODAY_ISO)

  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const id = session?.user.id ?? null
        const draft = id ? getPendingDraft(id) : null
        setUserId(id)
        // Whether the period this screen builds is the current one, known
        // once the real schedule loads below. A stored draft belongs to one
        // period, so it is only opened here when it is that period's: an
        // older draft for another one (a submitted next period, say) would
        // otherwise stand in for the schedule the agent asked to build.
        let draftBuildsCurrentPeriod = false
        if (id) {
          const [loadedHistory, loadedNextPeriod, loadedDefault] = await Promise.all([
            getMyScheduleDays(id, HISTORY_DATES),
            getMyScheduleDays(id, PAY_PERIOD_20_DATES),
            getMyScheduleDefault(id),
          ])
          if (cancelled) return
          const currentPeriodDays = loadedHistory.slice(PREVIOUS_PERIOD_DATES.length)
          // Nothing of the agent's own in the current period either: there is
          // no prior period to copy from, and no later draft yet to protect,
          // so, absent an explicit periodParam, this builds the current
          // period itself instead of the next one, starting from the
          // agent's normal schedule.
          const currentPeriodEmpty = !currentPeriodDays.some((day) => day.category !== 'unscheduled')
          const buildsCurrentPeriod = periodParam
            ? periodParam === 'current'
            : draft
              ? draft.periodStart === PAY_PERIOD_DATES[0].date
              : currentPeriodEmpty
          draftBuildsCurrentPeriod = buildsCurrentPeriod
          setHistory(loadedHistory)
          setScheduleDefault(loadedDefault)
          setTargetsCurrentPeriod(buildsCurrentPeriod)
          setExistingTargetDates(
            (buildsCurrentPeriod ? currentPeriodDays : loadedNextPeriod)
              .filter((day) => day.category !== 'unscheduled')
              .map((day) => day.date),
          )
          if (currentPeriodEmpty) {
            setSource('default')
          }
        }
        if (draft && draft.periodStart === (draftBuildsCurrentPeriod ? PAY_PERIOD_DATES[0].date : PAY_PERIOD_20_DATES[0].date)) {
          setSavedDraft(draft)
          setStep('review')
        }
      })
      .catch(() => {
        // No session to read; the source step still renders, and building
        // a draft without one is what already reports the failure below.
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [periodParam])

  // Loads the real returned submission behind reopenId, once, and builds
  // the same PendingDraft shape a saved draft already carries so the
  // review step's own editable-draft branch below needs no change to
  // render it. Skipped once savedDraft already carries this same
  // reopeningSubmissionId: a local edit already in progress this session
  // should never be clobbered by re-fetching the same real record.
  useEffect(() => {
    if (!sessionReady || !userId || !reopenId) return
    if (savedDraft?.reopeningSubmissionId === reopenId) return
    let cancelled = false
    getMyScheduleSubmissions(userId).then(async (submissions) => {
      const submission = submissions.find((candidate) => candidate.id === reopenId)
      if (cancelled || !submission || (submission.state !== 'returned' && submission.state !== 'submitted')) return
      const days = await getEditableScheduleSubmissionDays(userId, reopenId)
      if (cancelled || !days) return
      // Named from the submission's own periodStart, not from whichever
      // period this screen would otherwise build: a returned submission may
      // be for the current period or the next one, and which one that is
      // can differ from what a fresh build targets by the time it is
      // reopened.
      const periodLabel =
        submission.periodStart === PAY_PERIOD_DATES[0].date
          ? PAY_PERIOD_19.label
          : submission.periodStart === PAY_PERIOD_20_DATES[0].date
            ? PAY_PERIOD_20.label
            : TARGET_PERIOD.label
      const draft: PendingDraft = {
        periodLabel,
        periodStart: submission.periodStart,
        days,
        state: 'draft',
        reopeningSubmissionId: reopenId,
      }
      savePendingDraft(userId, draft)
      setSavedDraft(draft)
      setReopenContext({ comment: submission.decisionComment, decidedByName: submission.decidedByName, awaitingDecision: submission.state === 'submitted' })
      setStep('review')
    })
    return () => {
      cancelled = true
    }
  }, [sessionReady, userId, reopenId, savedDraft?.reopeningSubmissionId, TARGET_PERIOD.label])
  // Whether Continue has been used to build a fresh preview, as opposed to
  // just landing on the saved draft. Kept separate from `savedDraft` itself:
  // that state doubles as the conflict list's source of truth below, so
  // clearing it to switch views would also erase what a rebuild needs to
  // check itself against.
  const [rebuilding, setRebuilding] = useState(false)
  // A submitted schedule is locked: the back button below goes to the
  // dashboard here instead of Source, since Source leads to Continue and
  // Save Draft, which would silently overwrite a schedule already
  // awaiting a supervisor's decision, and there is no way back to draft
  // once submitSchedule has actually written it.
  const viewingSubmittedSchedule = savedDraft !== null && !rebuilding && savedDraft.state === 'submitted'
  // Set only when a save attempt actually fails, so the agent finds out
  // before leaving the screen with nothing durable behind the confirmation
  // they would otherwise see on the dashboard.
  const [saveFailed, setSaveFailed] = useState(false)
  // Set when Submit's own real write (submitSchedule) fails, or storage
  // itself would not take the write: either way, the agent finds out
  // before leaving with nothing actually submitted.
  const [submitFailed, setSubmitFailed] = useState(false)
  // The server's reason for a refused submit, when it gave one.
  const [submitMessage, setSubmitMessage] = useState<string | null>(null)
  // The first day of the agent's team placement: no schedule day comes before
  // it, so a new agent's pay period starts there.
  const [placementStart, setPlacementStart] = useState<string | null>(null)
  useEffect(() => {
    if (!userId) return
    let cancelled = false
    getMyPlacementStart(userId).then((start) => {
      if (!cancelled) setPlacementStart(start)
    })
    return () => {
      cancelled = true
    }
  }, [userId])
  // Disables Submit for the round trip submitSchedule now makes: unlike
  // Save Draft's own sessionStorage write, this is a real network call, so
  // a second tap before the first resolves would otherwise try to submit
  // the same period twice.
  const [submitting, setSubmitting] = useState(false)
  // docs/product/scheduling-model.md requires asking before overwriting
  // future draft entries, not just listing the conflicts: this holds the
  // write until a second, explicit action confirms it, so a single
  // accidental Save Draft click cannot erase what is already there.
  const [confirmingReplace, setConfirmingReplace] = useState(false)
  // Dates currently chosen for a quick-select status, cleared once applied.
  // Selecting one day and selecting several are the same mechanism here:
  // journey-map.md's "tap individual days... or select several days and
  // apply one change to all of them" is one continuum, not two separate
  // interactions.
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set())
  // Keyed by date rather than nested in the preview itself: the preview is
  // rebuilt fresh from source/customStart/customEnd on every render, so an
  // edit has to live in state that survives that recomputation and gets
  // layered back on afterward.
  const [edits, setEdits] = useState<Record<string, QuickEdit>>({})
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  // A custom range is read against the agent's own schedule entries as it is
  // chosen; a date with none is a gap (undefined). Kept with the range it was
  // read for, so a result for an earlier range is never shown for a later one.
  const customRangeKey = `${customStart}|${customEnd}`
  const [customFetched, setCustomFetched] = useState<{ key: string; days: (DayEntry | undefined)[] } | null>(null)
  useEffect(() => {
    const dates = customDateSkeletons(customStart, customEnd)
    if (!userId || dates.length === 0) return
    let cancelled = false
    getMyScheduleDays(userId, dates).then((days) => {
      if (!cancelled) {
        setCustomFetched({ key: `${customStart}|${customEnd}`, days: days.map((day) => (day.category === 'unscheduled' ? undefined : day)) })
      }
    })
    return () => {
      cancelled = true
    }
  }, [userId, customStart, customEnd])
  const customSourceData = customFetched?.key === customRangeKey ? customFetched.days : []
  const customRangeOrdered = customStart !== '' && customEnd !== ''
  const customRangeInvalid = customRangeOrdered && customEnd < customStart
  // Every date inside the range needs a schedule entry behind it, not just
  // some of them: a range that only partly overlaps the loaded data (Sep
  // 1-14, where real data starts Sep 8) would otherwise let buildPreview
  // quietly backfill the unbacked days from the agent's normal week while the review
  // step claims the whole range was copied.
  // Neither unbacked nor ready until the read for this exact range has come
  // back, so a valid range never flashes an error while it loads.
  const customRangeLoaded = customFetched?.key === customRangeKey
  const customRangeUnbacked =
    customRangeOrdered && !customRangeInvalid && customRangeLoaded && customSourceData.some((day) => !day)
  const customRangeReady = customRangeOrdered && !customRangeInvalid && customRangeLoaded && !customRangeUnbacked
  const canContinue = !TARGET_PERIOD_IS_PAST && (source !== 'custom' || customRangeReady)

  // The custom card's own detail text: what it shows depends on the dates
  // typed into it, never on which source is currently selected, so this
  // does not gate on `source === 'custom'` the way sourceDetail below does.
  // Reflects any ordered pair, not only one Continue will accept, so the
  // card still shows what the agent actually chose while the error
  // message below explains why it is not enough to proceed.
  const customCardDetail =
    customRangeOrdered && !customRangeInvalid
      ? `${formatDateShort(customStart)}–${formatDateShort(customEnd)}`
      : 'Choose your own dates'

  const sourceDetail = source === 'custom' ? customCardDetail : SOURCE_DETAIL[source]

  // The agent's own real days behind each option: the current period, its
  // second week, or all 28 days. An option with none of the agent's own
  // entries behind it is not selectable.
  const periodSource = history.slice(PREVIOUS_PERIOD_DATES.length)
  const sourceDaysByKey: Record<'period' | 'week' | 'month', DayEntry[]> = {
    period: periodSource,
    week: periodSource.slice(7),
    month: history,
  }
  const sourceHasData = (key: 'period' | 'week' | 'month') =>
    sourceDaysByKey[key].some((day) => day.category !== 'unscheduled')

  const sourceDays =
    source === 'custom' ? customSourceData : source === 'default' ? [] : sourceDaysByKey[source]

  const quickStatuses = quickStatusesFor(scheduleDefault?.shift?.time ?? null)
  const preview = buildPreview(sourceDays, scheduleDefault, TARGET_DATES)

  // The preview with any quick-select edits layered on top, by date. Every
  // field an edit touches is set explicitly (detail included, even when
  // absent) so a later edit to the same day never leaves a stale value
  // behind from an earlier one.
  const editedPreview = preview.map((entry) => {
    const edit = edits[entry.date]
    return edit ? { ...entry, category: edit.category, label: edit.label, detail: edit.detail } : entry
  })

  // Anything the target period already holds counts as a conflict per the
  // copying spec, not just the one seed date: once the agent has saved a
  // draft here, that saved draft covers every date, and building a fresh
  // copy would overwrite all of it, not only the date that conflicted
  // before anything had been saved.
  const existingDates = savedDraft ? savedDraft.days.map((entry) => entry.date) : existingTargetDates
  const conflictEntries = editedPreview.filter((entry) => existingDates.includes(entry.date))

  // The same quick-select edits, layered onto the saved draft itself rather
  // than a freshly built preview: reopening a saved draft is not a rebuild,
  // so it needs its own path to stay editable without going through
  // Source and Continue again.
  const editedSavedDraftDays = savedDraft
    ? savedDraft.days
        .filter((entry) => placementStart === null || entry.date >= placementStart)
        .map((entry) => {
          const edit = edits[entry.date]
          return edit ? { ...entry, category: edit.category, label: edit.label, detail: edit.detail } : entry
        })
    : []
  const hasSavedDraftEdits = savedDraft !== null && Object.keys(edits).length > 0

  function handleContinue() {
    // A fresh build supersedes the saved-draft view; the review step now
    // shows the newly computed preview, not the old saved draft, until this
    // one is itself saved. `savedDraft` stays put so its dates still count
    // toward conflict detection below. Edits and the current selection
    // belonged to whatever was being reviewed before; a new build starts
    // clean rather than layering them onto a possibly different baseline.
    setRebuilding(true)
    setConfirmingReplace(false)
    setSelectedDates(new Set())
    setEdits({})
    setStep('review')
  }

  function toggleDaySelection(date: string) {
    setSelectedDates((current) => {
      const next = new Set(current)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }

  function applyQuickStatus(patch: QuickEdit) {
    setEdits((current) => {
      const next = { ...current }
      for (const date of selectedDates) {
        // Work's assignment depends on which weekday each selected date
        // actually falls on, so it is filled in per date rather than
        // shared from one patch object applied to every selected day.
        next[date] = patch.category === 'work' ? { ...patch, detail: defaultDayFor(date, scheduleDefault).detail } : patch
      }
      return next
    })
    setSelectedDates(new Set())
    // The draft this would have replaced just changed underneath the
    // pending confirmation; asking again against the new preview beats
    // carrying a confirmation that was for a different set of days.
    setConfirmingReplace(false)
  }

  function handleSaveDraft() {
    if (TARGET_PERIOD_IS_PAST) return
    if (conflictEntries.length > 0 && !confirmingReplace) {
      // First click asks; it does not write anything yet.
      setConfirmingReplace(true)
      return
    }

    const draft: PendingDraft = {
      periodLabel: TARGET_PERIOD.label,
      periodStart: TARGET_DATES[0].date,
      days: editedPreview,
      state: 'draft',
    }
    if (!userId || !savePendingDraft(userId, draft)) {
      // Storage did not take the write (or, same effect, there is no
      // signed-in agent to key it by); staying put and saying so beats
      // navigating to a dashboard confirmation for a draft that will not
      // actually be there to reopen.
      setSaveFailed(true)
      return
    }
    navigate('/agent', { state: { draftSaved: true, periodLabel: TARGET_PERIOD.label } })
  }

  // Editing the agent's own already-saved draft in place, not copying over
  // it from another source, so this skips the conflict-confirmation gate
  // above: there is nothing here being replaced out from under the agent,
  // only what they just changed themselves.
  function handleSaveDraftEdits() {
    if (TARGET_PERIOD_IS_PAST) return
    if (!savedDraft || !userId) {
      setSaveFailed(true)
      return
    }
    const updated: PendingDraft = {
      periodLabel: savedDraft.periodLabel,
      periodStart: savedDraft.periodStart,
      days: editedSavedDraftDays,
      state: 'draft',
    }
    if (!savePendingDraft(userId, updated)) {
      setSaveFailed(true)
      return
    }
    setSavedDraft(updated)
    setEdits({})
    // Unlike handleSaveDraft's success path, this one stays on the page
    // instead of navigating away, so a stale failure from an earlier
    // attempt would otherwise keep showing after this one succeeds.
    setSaveFailed(false)
  }

  // Writes whatever is currently shown, edited or not, to the real
  // schedule_entries and schedule_submissions tables, then marks it
  // submitted locally in the same shape: waiting on a separate Save
  // Draft click first would mean an agent could submit a stale version
  // of a day they just changed. Every day the schedule holds is stamped
  // 'submitted' too, the same as pendingDraftStore's own
  // submitPendingDraft, so the day cards read the same way whichever
  // path submitted them. A draft carrying its own reopeningSubmissionId
  // is an existing submission being edited, not a fresh one: that
  // goes through reopenSchedule instead, the only path that may write to
  // a submission already on record rather than insert a new one.
  async function handleSubmitSavedDraft() {
    if (TARGET_PERIOD_IS_PAST || submitting) return
    if (!savedDraft || !userId) {
      setSubmitFailed(true)
      return
    }
    setSubmitting(true)
    const submitted = savedDraft.reopeningSubmissionId
      ? await reopenSchedule(savedDraft.reopeningSubmissionId, savedDraft.periodStart, editedSavedDraftDays)
      : await submitSchedule(savedDraft.periodStart, editedSavedDraftDays)
    setSubmitting(false)
    if (!submitted.ok) {
      setSubmitMessage(submitted.message)
      setSubmitFailed(true)
      return
    }
    const updated: PendingDraft = {
      periodLabel: savedDraft.periodLabel,
      periodStart: savedDraft.periodStart,
      days: editedSavedDraftDays.map((day) => ({ ...day, state: 'submitted' })),
      state: 'submitted',
    }
    if (!savePendingDraft(userId, updated)) {
      setSubmitFailed(true)
      return
    }
    navigate('/agent', { state: { draftSubmitted: true, periodLabel: savedDraft.periodLabel } })
  }

  if (!sessionReady) {
    return (
      <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#copy-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="copy-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-8">
          {step === 'source' || viewingSubmittedSchedule ? (
            <Link
              to="/agent"
              className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
            >
              <ArrowLeftIcon />
              Dashboard
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setStep('source')}
              className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
            >
              <ArrowLeftIcon />
              Source
            </button>
          )}

          {step === 'source' ? (
            <>
              <div>
                <h1 className="text-h3 font-extrabold lg:text-h2">Build {TARGET_PERIOD.label}</h1>
                <p className="mt-0.5 text-small text-ink-muted">{TARGET_PERIOD.range}</p>
                {TARGET_PERIOD_IS_PAST && (
                  <p className="mt-2 text-caption font-semibold text-error">
                    {TARGET_PERIOD.label} has passed; there is no later period to build yet.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                {SOURCE_ORDER.map((key) =>
                  key === 'custom' ? (
                    <SourceCard
                      key={key}
                      label={SOURCE_LABEL.custom}
                      detail={customCardDetail}
                      selected={source === 'custom'}
                      onSelect={() => setSource('custom')}
                    >
                      {source === 'custom' && (
                        <div className="mt-3 flex flex-col gap-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label htmlFor="copy-custom-start" className="mb-1 block text-caption font-bold text-ink-muted">
                                Start date
                              </label>
                              <input
                                id="copy-custom-start"
                                type="date"
                                value={customStart}
                                onChange={(event) => setCustomStart(event.target.value)}
                                className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                              />
                            </div>
                            <div>
                              <label htmlFor="copy-custom-end" className="mb-1 block text-caption font-bold text-ink-muted">
                                End date
                              </label>
                              <input
                                id="copy-custom-end"
                                type="date"
                                value={customEnd}
                                onChange={(event) => setCustomEnd(event.target.value)}
                                className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                              />
                            </div>
                          </div>
                          {customRangeInvalid && (
                            <p className="text-caption font-semibold text-error">
                              End date must be on or after the start date.
                            </p>
                          )}
                          {customRangeUnbacked && (
                            <p className="text-caption font-semibold text-error">
                              Some of these dates have no schedule of yours. Choose a range where every day has one.
                            </p>
                          )}
                        </div>
                      )}
                    </SourceCard>
                  ) : (
                    <SourceCard
                      key={key}
                      label={SOURCE_LABEL[key]}
                      detail={SOURCE_DETAIL[key]}
                      selected={source === key}
                      onSelect={() => setSource(key)}
                      disabled={key !== 'default' && !sourceHasData(key)}
                    />
                  ),
                )}
              </div>
            </>
          ) : viewingSubmittedSchedule ? (
            <>
              <div>
                <h1 className="text-h3 font-extrabold lg:text-h2">{TARGET_PERIOD.label}</h1>
                <p className="mt-0.5 text-small text-ink-muted">
                  {TARGET_PERIOD.range} &middot; submitted, waiting on your supervisor&apos;s decision
                </p>
              </div>

              <HoursSummary {...hoursSummaryRows(savedDraft.days)} compareToTourOfDuty />

              <div className="flex flex-col gap-2">
                {savedDraft.days.map((entry) => (
                  <DayCard key={entry.date} entry={entry} />
                ))}
              </div>
            </>
          ) : savedDraft && !rebuilding ? (
            <>
              <div>
                <h1 className="text-h3 font-extrabold lg:text-h2">{TARGET_PERIOD.label}</h1>
                <p className="mt-0.5 text-small text-ink-muted">
                  {TARGET_PERIOD.range} &middot;{' '}
                  {savedDraft.reopeningSubmissionId
                    ? reopenContext?.awaitingDecision
                      ? 'submitted; edit until your supervisor decides'
                      : 'returned; edit and resubmit'
                    : 'saved draft, from your last copy'}
                </p>
                {TARGET_PERIOD_IS_PAST ? (
                  <p className="mt-2 text-caption font-semibold text-error">
                    {TARGET_PERIOD.label} has passed; saving or submitting changes to it is no longer available.
                  </p>
                ) : (
                  <p className="mt-2 text-caption text-ink-subtle">
                    Tap a day, or several, to change its status.
                  </p>
                )}
              </div>

              {savedDraft.reopeningSubmissionId && !reopenContext?.awaitingDecision && reopenContext?.comment && (
                <div className="rounded-surface border border-line bg-surface-2 p-3">
                  <p className="text-caption font-bold text-ink-muted">
                    Returned{reopenContext.decidedByName ? ` by ${reopenContext.decidedByName}` : ''}
                  </p>
                  <p className="mt-1 text-small">&ldquo;{reopenContext.comment}&rdquo;</p>
                </div>
              )}

              <HoursSummary {...hoursSummaryRows(editedSavedDraftDays)} compareToTourOfDuty />

              <div className="flex flex-col gap-2">
                {editedSavedDraftDays.map((entry) => (
                  <SelectableDayCard
                    key={entry.date}
                    entry={entry}
                    selected={selectedDates.has(entry.date)}
                    flagged={false}
                    onToggle={() => toggleDaySelection(entry.date)}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <div>
                <h1 className="text-h3 font-extrabold lg:text-h2">Review the copy</h1>
                <p className="mt-0.5 text-small text-ink-muted">
                  {TARGET_PERIOD.label} &middot; {TARGET_PERIOD.range}, copied from {sourceDetail}
                </p>
                <p className="mt-2 text-caption text-ink-subtle">
                  Tap a day, or several, to change its status.
                </p>
              </div>

              <HoursSummary {...hoursSummaryRows(editedPreview)} compareToTourOfDuty />

              {conflictEntries.length > 0 && (
                <div className="flex gap-2.5 rounded-surface border border-warning/35 bg-warning/10 p-3">
                  <span className="flex-none text-warning">
                    <TriangleAlertIcon />
                  </span>
                  <p className="text-caption text-ink">
                    <span className="font-bold">
                      {conflictEntries.length === 1
                        ? '1 existing entry'
                        : `${conflictEntries.length} existing entries`}{' '}
                      will be replaced.
                    </span>{' '}
                    {conflictEntries.length === 1 ? (
                      <>
                        {conflictEntries[0].weekday}, {conflictEntries[0].month} {conflictEntries[0].day} already has
                        an entry; copying overwrites it.
                      </>
                    ) : (
                      <>
                        {TARGET_PERIOD.label} already has a schedule; copying replaces all of it.
                      </>
                    )}
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-2">
                {editedPreview.map((entry) => (
                  <SelectableDayCard
                    key={entry.date}
                    entry={entry}
                    selected={selectedDates.has(entry.date)}
                    flagged={existingDates.includes(entry.date)}
                    onToggle={() => toggleDaySelection(entry.date)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="sticky bottom-0 flex-none border-t border-line bg-canvas p-4 pb-[max(1rem,env(safe-area-inset-bottom))] lg:p-6">
          <div className="mx-auto w-full max-w-2xl">
            {step === 'source' ? (
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
                className="w-full rounded-control bg-primary px-4 py-3.5 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
              >
                Continue
              </button>
            ) : selectedDates.size > 0 ? (
              <div className="flex flex-col gap-2">
                <p aria-live="polite" className="text-center text-caption font-bold text-ink-muted">
                  {selectedDates.size} {selectedDates.size === 1 ? 'day' : 'days'} selected
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {quickStatuses.map((status) => (
                    <button
                      key={status.key}
                      type="button"
                      onClick={() => applyQuickStatus(status.patch)}
                      disabled={status.disabled}
                      className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-1 text-caption font-bold hover:bg-surface-2 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
                {scheduleDefault && !scheduleDefault.shift && (
                  <p className="text-center text-caption text-ink-muted">
                    Work is not available until your supervisor sets your shift hours.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedDates(new Set())}
                  className="flex min-h-11 items-center justify-center rounded-control text-small font-bold text-ink-muted"
                >
                  Clear selection
                </button>
              </div>
            ) : viewingSubmittedSchedule ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/agent')}
                  className="w-full rounded-control bg-primary px-4 py-3.5 text-small font-bold text-primary-ink hover:bg-primary-hover"
                >
                  Done
                </button>
              </>
            ) : savedDraft && !rebuilding ? (
              <>
                {saveFailed && (
                  <p role="alert" className="mb-2 text-caption font-semibold text-error">
                    Could not save your changes. Storage may be unavailable in this browser; try again or use a
                    different browser.
                  </p>
                )}
                {submitFailed && (
                  <p role="alert" className="mb-2 text-caption font-semibold text-error">
                    {submitMessage
                      ? `Could not submit the schedule. ${submitMessage}.`
                      : 'Could not submit the schedule. Storage may be unavailable in this browser; try again or use a different browser.'}
                  </p>
                )}
                <div className="flex gap-2">
                  {hasSavedDraftEdits ? (
                    <button
                      type="button"
                      onClick={handleSaveDraftEdits}
                      disabled={TARGET_PERIOD_IS_PAST}
                      className="flex-1 rounded-control border border-line-strong px-4 py-3.5 text-small font-bold text-ink-muted hover:bg-surface-2 disabled:opacity-40"
                    >
                      Save changes
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate('/agent')}
                      className="flex-1 rounded-control border border-line-strong px-4 py-3.5 text-small font-bold text-ink-muted hover:bg-surface-2"
                    >
                      Done
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSubmitSavedDraft}
                    disabled={TARGET_PERIOD_IS_PAST || submitting}
                    className="flex-1 rounded-control bg-primary px-4 py-3.5 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
                  >
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                </div>
              </>
            ) : (
              <>
                {saveFailed && (
                  <p role="alert" className="mb-2 text-caption font-semibold text-error">
                    Could not save the draft. Storage may be unavailable in this browser; try again or use a
                    different browser.
                  </p>
                )}
                {confirmingReplace ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmingReplace(false)}
                      className="flex-1 rounded-control border border-line-strong px-4 py-3.5 text-small font-bold text-ink-muted hover:bg-surface-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={TARGET_PERIOD_IS_PAST}
                      className="flex-1 rounded-control bg-primary px-4 py-3.5 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
                    >
                      Replace draft
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={TARGET_PERIOD_IS_PAST}
                    className="w-full rounded-control bg-primary px-4 py-3.5 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
                  >
                    Save Draft
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
